import { LngLat, LngLatBounds } from 'mapbox-gl';
import type Geometry from '@arcgis/core/geometry/Geometry';
import Extent from '@arcgis/core/geometry/Extent.js';
import type { GeometryUnion } from '@arcgis/core/geometry/types.js';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils.js';

import { arcgisToGeoJSON } from '@terraformer/arcgis';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import { ViewHitTestResult } from '@arcgis/core/views/types';
import VectorTileGraphicOrigin from '@arcgis/core/graphic/VectorTileGraphicOrigin';
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';
import type { HoverPopupFeature, SelectedAsset } from '../types';
import { selectedAssetToHoverPopupFeature } from './hoverPopupNormalization';
import { resolveSelectionType } from './assetCategory';

class EsriUtils {
	static selectedAssetToHoverPopupFeature(asset: SelectedAsset): HoverPopupFeature {
		return selectedAssetToHoverPopupFeature(asset);
	}

	static boundsToExtent(bounds: LngLatBounds): Extent {
		return new Extent({
			xmax: bounds.getNorthEast().lng,
			ymax: bounds.getNorthEast().lat,
			xmin: bounds.getSouthWest().lng,
			ymin: bounds.getSouthWest().lat,
			spatialReference: { wkid: 4326 },
		});
	}

	/**
	 * Converts an ArcGIS {@link Extent} to `[xmin, ymin, xmax, ymax]` in WGS84 geographic coordinates
	 * (west longitude, south latitude, east longitude, north latitude), matching {@link boundsToExtent}.
	 * Web Mercator (3857) extents are projected to geographic first; extents already in a geographic
	 * spatial reference are returned using their envelope in degrees.
	 */
	static extentToBounds(extent: Extent): [number, number, number, number] {
		const sr = extent.spatialReference;
		let geo: Extent = extent;
		if (sr?.isWebMercator || sr?.wkid === 3857) {
			geo = webMercatorUtils.webMercatorToGeographic(
				extent as GeometryUnion,
			) as Extent;
		}
		return [geo.xmin, geo.ymin, geo.xmax, geo.ymax];
	}

	static lngLatToCenter(lngLat: { lng: number; lat: number }): number[] {
		return [lngLat.lng, lngLat.lat];
	}

	static lngLatToPoint(lngLat: { lng: number; lat: number }): Point {
		return new Point({
			x: lngLat.lng,
			y: lngLat.lat,
			spatialReference: { wkid: 4326 },
		});
	}

	static pointToLngLat(point: Point): LngLat {
		if (point.spatialReference.isGeographic) {
			return new LngLat(
				point.longitude as number,
				point.latitude as number,
			);
		} else {
			const geographicPoint = webMercatorUtils.webMercatorToGeographic(
				point,
			) as Point;
			return new LngLat(
				geographicPoint.longitude as number,
				geographicPoint.latitude as number,
			);
		}
	}
	/**
	 * Converts an ArcGIS Geometry to GeoJSON.Geometry using @terraformer/arcgis.
	 * Accepts either an ArcGIS Geometry instance or an existing ArcGIS REST JSON object.
	 * If the geometry is an instance in Web Mercator, it is converted to geographic first.
	 */
	static esriGeometryToGeoJSON(
		geometry: Geometry | null | undefined,
	): GeoJSON.Geometry | null {
		if (geometry == null) return null;

		let geometryToSerialize = geometry;
		const isInstance =
			typeof (geometry as { toJSON?: () => unknown }).toJSON ===
			'function';
		if (isInstance) {
			const geom = geometry as Geometry;
			if (
				geom.spatialReference?.isWebMercator ||
				geom.spatialReference?.wkid === 3857
			) {
				geometryToSerialize = webMercatorUtils.webMercatorToGeographic(
					geom as GeometryUnion,
				) as Geometry;
			}
		}

		const arcgisJson = isInstance
			? (geometryToSerialize as Geometry).toJSON()
			: (geometryToSerialize as Parameters<typeof arcgisToGeoJSON>[0]);

		return arcgisToGeoJSON(arcgisJson);
	}

	static viewHitTestResultsToSelectedAssets(
		hitTestResult: ViewHitTestResult,
	): SelectedAsset[] {
		const results: SelectedAsset[] = [];
		for (const result of hitTestResult.results) {
			if (result.type !== 'graphic') continue;

			const graphic: Graphic = result.graphic;

			const esriGeometry = graphic?.geometry as Geometry | undefined;

			const origin = graphic?.origin as
				| VectorTileGraphicOrigin
				| undefined;
			const layer = result?.layer as
				| { type?: string; id?: string }
				| undefined;

			const layerId = layer?.id;
			const originLayerId = origin?.layerId;
			if (
				originLayerId?.endsWith('-selected') ||
				originLayerId?.endsWith('-highlight-background') ||
				originLayerId?.endsWith('-highlight-foreground')
			) {
				// don't include selected, highlight-background, or highlight-foreground layers
				continue;
			}
			let styleLayer: Record<string, unknown> | undefined;
			if (originLayerId && layerId && result.layer) {
				const vtl = result.layer as VectorTileLayer & { loaded?: boolean };
				if (!vtl.loaded) continue;
				styleLayer = vtl.getStyleLayer(originLayerId) as
					| Record<string, unknown>
					| undefined;
				if (
					(styleLayer as { layout?: { visibility?: string } } | undefined)
						?.layout?.visibility === 'none'
				) {
					// sdk is returning hidden layers, so we skip them
					continue;
				}
			}
			const attributes = (graphic?.attributes ?? {}) as Record<
				string,
				unknown
			>;

			const rawId =
				(attributes._id as string | undefined) ??
				(attributes.id as string | undefined) ??
				(attributes.assetId as string | undefined);
			const split =
				rawId && rawId.includes?.('#') ? rawId.split('#') : undefined;
			const styleSource = styleLayer?.source as string | undefined;
			const isSpatialStyleLayer = styleSource === 'tiles-other';
			const assetId = split?.[1] ?? rawId;
			const layerName =
				(attributes.layerName as string | undefined) ??
				(layer as { title?: string } | undefined)?.title;
			// One resolver for both fields, mirroring Mapbox `Feature.assetType`.
			// `attributes.type` is passed as the weak `featureType` rather than folded
			// into assetType: GeoJsonDataService stamps it 'asset' on every non-sensor
			// feature, which used to bury an incident's own `sequence` (I360P-1354) and
			// an id's '#' prefix.
			const { assetType, type: itemType } = resolveSelectionType({
				assetType: isSpatialStyleLayer
					? 'amSpatial'
					: (attributes.assetType as string | undefined),
				srcType: attributes.srcType as string | undefined,
				featureType: attributes.type as string | undefined,
				idPrefix: split?.[0],
				sequence: attributes.sequence,
			});

			if (layerId && assetId) {
				const rawIdString = rawId?.toString();
				const assetIdString = assetId?.toString();
				const isInstance =
					esriGeometry != null &&
					typeof (esriGeometry as { toJSON?: () => unknown }).toJSON ===
					'function';
				const serializedGeometry = isInstance
					? (esriGeometry as Geometry).toJSON()
					: esriGeometry;
				const asset: SelectedAsset = {
					...attributes,
					id: rawIdString ?? assetIdString,
					layerId: originLayerId ?? layerId ?? '',
					esriLayerId: layerId,
					layerName,
					sourceId: styleSource,
					assetId: assetIdString,
					assetType,
					type: itemType,
					esriGeometry: serializedGeometry as unknown as Geometry,
					geometry: EsriUtils.esriGeometryToGeoJSON(esriGeometry),
				} as unknown as SelectedAsset;
				results.push(asset);
			}
		}
		return results;
	}
}

export default EsriUtils;
